import { useEffect, useState } from 'react';
import { sendGetRequest, sendPostRequest } from '../../../actions/app-actions';
import { connect, useDispatch } from 'react-redux';
import { withRouter, notification } from '../../../utils/utility';
import { setCurrentUser } from '../../../actions/authActions';
import Loader from '../../../components/Loader/Loader';
import * as Yup from "yup";
import { Formik, Form } from "formik";
import isEmpty from "../../../validation/is-empty";

/* ─── Design tokens ─────────────────────────────────────────────────────── */
const C = {
  navy:        "#0c4767",
  navyLight:   "#e8f1f7",
  orange:      "#FE9920",
  orangeLight: "#FFF7ED",
  dark:        "#111827",
  mid:         "#374151",
  muted:       "#6B7280",
  faint:       "#9CA3AF",
  border:      "#E5E7EB",
  surface:     "#F9FAFB",
  white:       "#ffffff",
  green:       "#10B981",
  greenBg:     "#F0FDF4",
  greenBorder: "#BBF7D0",
  greenDark:   "#065F46",
  red:         "#EF4444",
  redBg:       "#FEF2F2",
  redBorder:   "#FECACA",
  redDark:     "#991B1B",
};

const Google2FASchema = Yup.object().shape({
  otp: Yup.string()
    .matches(/^\d+$/, 'OTP must contain digits only')
    .min(6, "OTP must be 6 digits")
    .max(6, "OTP must be 6 digits")
    .required("OTP is required"),
});

const STYLES = `
  @keyframes g2fa-spin { to { transform: rotate(360deg); } }
  .g2fa-input {
    width: 100%;
    border: 2px solid #E5E7EB;
    border-radius: 10px;
    padding: 12px 16px;
    font-size: 20px;
    letter-spacing: 6px;
    text-align: center;
    color: #111827;
    outline: none;
    background: #fff;
    transition: border-color 0.15s, box-shadow 0.15s;
    font-family: 'Courier New', monospace;
    font-weight: 700;
    box-sizing: border-box;
  }
  .g2fa-input:focus {
    border-color: #0c4767 !important;
    box-shadow: 0 0 0 3px rgba(12,71,103,0.15) !important;
  }
  .g2fa-input.is-error {
    border-color: #EF4444 !important;
  }
  .g2fa-input::placeholder {
    letter-spacing: 2px;
    font-size: 14px;
    font-weight: 400;
    color: #9CA3AF;
  }
  .g2fa-copy-btn:hover { background: #e8f1f7 !important; }
  .g2fa-btn-enable:not(:disabled):hover  { background: #0a3d55 !important; }
  .g2fa-btn-disable:not(:disabled):hover { background: #c0392b !important; }
`;

const Google2FA = (props) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData]           = useState(null);
  const [copied, setCopied]       = useState(false);

  useEffect(() => { get2FASetup(); }, []);

  const get2FASetup = () => {
    setIsLoading(true);
    props.sendGetRequest({
      url: `2fa/setup`,
      onSuccess: (response) => { setData(response); setIsLoading(false); },
      onError:   ()         => { setIsLoading(false); },
    });
  };

  const copyKey = () => {
    if (!data?.secret) return;
    navigator.clipboard.writeText(data.secret).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const is2FAEnabled = props.auth.user?.google_2fa_status === 'E';

  return (
    <>
      <style>{STYLES}</style>
      <Loader isShowLoader={isLoading} />

      <div style={{ padding: "32px 28px", maxWidth: "960px" }}>

        {/* ── Section header ──────────────────────────────────────── */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: "14px", marginBottom: "28px" }}>
          <div style={{
            width: 48, height: 48, borderRadius: "12px",
            background: is2FAEnabled ? C.greenBg : C.navyLight,
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
              stroke={is2FAEnabled ? C.green : C.navy}
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
              <h2 style={{ margin: 0, fontSize: "18px", fontWeight: 700, color: C.dark }}>
                Two-Factor Authentication (2FA)
              </h2>
              <span style={{
                display: "inline-flex", alignItems: "center", gap: "5px",
                padding: "3px 10px", borderRadius: "999px", fontSize: "12px", fontWeight: 600,
                background: is2FAEnabled ? C.greenBg    : "#F3F4F6",
                color:      is2FAEnabled ? C.greenDark  : C.muted,
                border:     `1px solid ${is2FAEnabled ? C.greenBorder : C.border}`,
              }}>
                <span style={{
                  width: 7, height: 7, borderRadius: "50%",
                  background: is2FAEnabled ? C.green : C.faint,
                  display: "inline-block",
                }} />
                {is2FAEnabled ? "Enabled" : "Disabled"}
              </span>
            </div>
            <p style={{ margin: "6px 0 0", fontSize: "13px", color: C.muted, lineHeight: 1.6 }}>
              Add an extra layer of security to your account. Once enabled, you'll need a
              6-digit code from your authenticator app on every login.
            </p>
          </div>
        </div>

        {/* ── Two-column grid: Step 1 | Step 2 (when disabled) ─────── */}
        {/* ── Single column: Disable form only (when enabled)   ─────── */}
        <Formik
          initialValues={{ otp: "" }}
          validationSchema={Google2FASchema}
          onSubmit={(values, formHelper) => {
            const { sendPostRequest } = props;
            setIsLoading(true);

            const url      = is2FAEnabled ? '/2fa/disable' : '/2fa/enable';
            const formData = is2FAEnabled
              ? { otp: values.otp }
              : { otp: values.otp, google_2fa_secret: data?.secret };

            sendPostRequest({
              url,
              data: formData,
              onSuccess: (response) => {
                setIsLoading(false);
                if (response.success) {
                  notification.success(response?.message);
                  const user = { ...props.auth.user };
                  user.google_2fa_status = is2FAEnabled ? 'D' : 'E';
                  dispatch(setCurrentUser(user));
                  if (user.google_2fa_status === 'D') { get2FASetup(); }
                  formHelper.resetForm();
                }
              },
              onError: (error) => {
                setIsLoading(false);
                const { data: responseData } = error.response;
                if (!responseData.success) {
                  if (isEmpty(responseData?.data) && !isEmpty(responseData?.message)) {
                    notification.error(responseData?.message);
                  }
                }
              },
            });
          }}
        >
          {({ values, errors, touched, handleChange }) => (
            <Form>
              <div style={{
                display: "grid",
                gridTemplateColumns: !is2FAEnabled && data ? "1fr 1fr" : "1fr",
                gap: "20px",
                alignItems: "start",
              }}>

                {/* ── LEFT: Step 1 — QR code + setup key ─────────── */}
                {!is2FAEnabled && data && (
                  <div style={{
                    background: C.surface,
                    border: `1px solid ${C.border}`,
                    borderRadius: "16px",
                    padding: "24px",
                    height: "100%",
                    boxSizing: "border-box",
                  }}>
                    {/* Step badge */}
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
                      <span style={{
                        width: 24, height: 24, borderRadius: "50%",
                        background: C.navy, color: C.white,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: "12px", fontWeight: 700, flexShrink: 0,
                      }}>1</span>
                      <p style={{ margin: 0, fontSize: "13px", fontWeight: 700, color: C.dark }}>
                        Scan QR code or enter key manually
                      </p>
                    </div>
                    <p style={{ margin: "0 0 16px", fontSize: "12.5px", color: C.muted, lineHeight: 1.6 }}>
                      Open Google Authenticator, Authy, or any TOTP app and scan the code below.
                    </p>

                    {/* QR code */}
                    {data.qrCodeSvg && (
                      <div style={{ display: "flex", justifyContent: "center", marginBottom: "20px" }}>
                        <div style={{
                          padding: "12px",
                          background: C.white,
                          border: `1px solid ${C.border}`,
                          borderRadius: "12px",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                          display: "inline-block",
                        }}>
                          <img
                            src={`data:image/svg+xml;base64,${data.qrCodeSvg}`}
                            alt="2FA QR Code"
                            style={{ width: 148, height: 148, display: "block" }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Setup key */}
                    <p style={{ margin: "0 0 7px", fontSize: "11px", fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.8px" }}>
                      Manual setup key
                    </p>
                    <div style={{
                      display: "flex", alignItems: "center", gap: "8px",
                      background: C.white, border: `1px solid ${C.border}`,
                      borderRadius: "10px", padding: "10px 12px",
                    }}>
                      <code style={{
                        flex: 1, fontSize: "13px", fontFamily: "'Courier New', monospace",
                        fontWeight: 700, color: C.navy, letterSpacing: "1.5px", wordBreak: "break-all",
                      }}>
                        {data.secret}
                      </code>
                      <button
                        type="button"
                        className="g2fa-copy-btn"
                        onClick={copyKey}
                        title="Copy setup key"
                        aria-label="Copy setup key"
                        style={{
                          flexShrink: 0, background: C.navyLight, border: "none",
                          borderRadius: "7px", padding: "5px 9px", cursor: "pointer",
                          display: "flex", alignItems: "center", gap: "4px",
                          fontSize: "11px", fontWeight: 600,
                          color: copied ? C.green : C.navy, transition: "background 0.15s",
                        }}
                      >
                        {copied ? (
                          <>
                            <svg width="12" height="12" viewBox="0 0 20 20" fill={C.green}><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
                            Copied!
                          </>
                        ) : (
                          <>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={C.navy} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
                            </svg>
                            Copy
                          </>
                        )}
                      </button>
                    </div>
                    <p style={{ margin: "7px 0 0", fontSize: "11px", color: C.faint, lineHeight: 1.5 }}>
                      Refreshes every 30 seconds — use the current key.
                    </p>
                  </div>
                )}

                {/* ── RIGHT: Step 2 — OTP input + action button ───── */}
                <div style={{
                  background: is2FAEnabled ? C.redBg : C.white,
                  border: `1px solid ${is2FAEnabled ? C.redBorder : C.border}`,
                  borderRadius: "16px",
                  padding: "24px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  boxSizing: "border-box",
                }}>
                  {/* Step badge */}
                  {!is2FAEnabled && (
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
                      <span style={{
                        width: 24, height: 24, borderRadius: "50%",
                        background: C.navy, color: C.white,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: "12px", fontWeight: 700, flexShrink: 0,
                      }}>2</span>
                      <p style={{ margin: 0, fontSize: "13px", fontWeight: 700, color: C.dark }}>
                        Verify &amp; Enable
                      </p>
                    </div>
                  )}

                  {/* Disable heading */}
                  {is2FAEnabled && (
                    <p style={{ margin: "0 0 6px", fontSize: "13px", fontWeight: 700, color: C.redDark }}>
                      Disable Two-Factor Authentication
                    </p>
                  )}

                  <p style={{ margin: "0 0 20px", fontSize: "13px", color: C.mid, lineHeight: 1.6 }}>
                    {is2FAEnabled
                      ? "Enter your current 6-digit code to confirm disabling 2FA."
                      : "Enter the 6-digit code shown in your authenticator app."}
                  </p>

                  {/* OTP input */}
                  <div style={{ marginBottom: "20px" }}>
                    <label
                      htmlFor="g2fa-otp"
                      style={{ display: "block", fontSize: "12px", fontWeight: 600, color: C.mid, marginBottom: "8px" }}
                    >
                      Authenticator code
                    </label>
                    <input
                      id="g2fa-otp"
                      name="otp"
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      value={values.otp}
                      onChange={handleChange}
                      placeholder="○○○○○○"
                      aria-describedby={errors.otp && touched.otp ? "g2fa-otp-err" : undefined}
                      aria-invalid={!!(errors.otp && touched.otp)}
                      className={`g2fa-input${errors.otp && touched.otp ? " is-error" : ""}`}
                    />
                    {errors.otp && touched.otp && (
                      <p id="g2fa-otp-err" role="alert" style={{ margin: "6px 0 0", fontSize: "12px", color: C.red }}>
                        {errors.otp}
                      </p>
                    )}
                  </div>

                  {/* Submit button */}
                  {is2FAEnabled ? (
                    <button
                      type="submit"
                      className="g2fa-btn-disable"
                      style={{
                        width: "100%", background: C.red, color: C.white, border: "none",
                        borderRadius: "10px", padding: "12px 20px",
                        fontSize: "14px", fontWeight: 700, cursor: "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                        transition: "background 0.15s",
                        boxShadow: "0 2px 8px rgba(239,68,68,0.3)",
                      }}
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
                      </svg>
                      Disable 2FA
                    </button>
                  ) : (
                    <button
                      type="submit"
                      className="g2fa-btn-enable"
                      style={{
                        width: "100%", background: C.navy, color: C.white, border: "none",
                        borderRadius: "10px", padding: "12px 20px",
                        fontSize: "14px", fontWeight: 700, cursor: "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                        transition: "background 0.15s",
                        boxShadow: "0 2px 8px rgba(12,71,103,0.28)",
                      }}
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/>
                      </svg>
                      Enable 2FA
                    </button>
                  )}
                </div>

              </div>
            </Form>
          )}
        </Formik>

        {/* Security note */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: "8px", marginTop: "20px", padding: "12px 16px", background: C.navyLight, borderRadius: "10px" }}>
          <svg width="15" height="15" viewBox="0 0 20 20" fill={C.navy} style={{ flexShrink: 0, marginTop: "1px" }}>
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"/>
          </svg>
          <p style={{ margin: 0, fontSize: "12px", color: C.navy, lineHeight: 1.6 }}>
            We recommend using <strong>Google Authenticator</strong> or <strong>Authy</strong>.
            Store your backup codes in a safe place in case you lose access to your device.
          </p>
        </div>

      </div>
    </>
  );
};

const mapStateToProps = (state) => ({ auth: state.auth });

export default connect(mapStateToProps, { sendGetRequest, sendPostRequest })(withRouter(Google2FA));
